import { ActionButton, ChoiceGroup, DatePicker, DefaultButton, DetailsList, Dialog, DialogFooter, DirectionalHint, Dropdown, IColumn, Icon, IconButton, Link, Panel, PanelType, PrimaryButton, SelectionMode, Stack, TooltipHost } from '@fluentui/react';
import React, { } from 'react';
import styles from './styles.module.scss';

// assets

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import { NavLink } from 'react-router-dom';
import LoadingComponent from '../../../feedbacks/loading';
import { faChevronDown, faClose, faFile, faFileExcel, faFilePdf, faFilePowerpoint, faFileWord, faImage, faUpload, faWarning } from '@fortawesome/pro-light-svg-icons';
import FileFormats, { TFileType } from '../../../../manifests/fileFormats';
import GeneralService from '../../../../services/general';
import Dropzone, { useDropzone } from 'react-dropzone';
import { DocumentvisiblityType, IDocumentProps, IFileDetailsProps, IFileProps } from '../../../../props/general';
import DocumentTypes, { VendorDocumentTypes } from '../../../../manifests/documentTypes';
import { useId } from '@fluentui/react-hooks';
import DocumentVisibilities from '../../../../manifests/documentVisibilities';
import { IInternalDocumentResourceShort } from '../../../../props/internalDocuments';
import { useStore } from '../../../../stores/root';
import ErrorService from '../../../../services/general/error';

import { getFileTypeIconProps } from '@fluentui/react-file-type-icons';
import moment from 'moment';

export type TTagVariant = 'default' | 'active' | 'success' | 'light' | 'error' | 'warning' | 'transparent';


type IUploadFilesPanelProps = {
	title?: string;
	multiple?: boolean;
	limit?: number;
	maxSize?: number;
	allowedTypes?: TFileType[];
	existings?: IDocumentProps[] | IInternalDocumentResourceShort[];
	onUpload(files: IFileDetailsProps[]): void;
	onCancel(): void;
	useVendorDocumentType?: boolean;
	documentTypes?: DocumentTypeProps[];
	allowSelectDocumentType?: boolean;
}

export type DocumentTypeProps = {
	key: string;
	text: string;
	variant?: TTagVariant;
	isDefault?: boolean;
}

const UploadFilesPanel: React.FC<IUploadFilesPanelProps> = (props: IUploadFilesPanelProps) => {
	const { banner } = useStore();
	const [uploading, setUploading] = React.useState<boolean>();
	const [files, setFiles] = React.useState<IFileDetailsProps[]>([]);

	const columns: IColumn[] = [
		{
			key: 'icon',
			name: 'icon',
			iconName: "Page",
			isIconOnly: true,
			minWidth: 20,
			maxWidth: 20,
			onRender: (file: IFileDetailsProps) => {
				const extension = ("." + file.data.name.split('.').pop()).toLowerCase();
				return <Stack styles={{ root: { paddingTop: 6, paddingBottom: 6 } }}>
					<Icon {...getFileTypeIconProps({ extension, size: 20, imageFileType: 'svg' })} />
				</Stack>
			}
		},
		{
			key: 'name',
			name: 'Name',
			minWidth: 100,
			onRender: (file: IFileDetailsProps) => {
				return <Stack styles={{ root: { paddingTop: 8, paddingBottom: 8 } }}>
					<Text size={'small'}>{file.data.name}</Text>
				</Stack>
			}
		},
		{
			key: 'type',
			name: 'Type',
			minWidth: 150,
			maxWidth: 150,
			onRender: (file: IFileDetailsProps, idx: number = 0) => {
				const types = props.useVendorDocumentType ? VendorDocumentTypes : props.documentTypes || DocumentTypes;
				const selectedKey = props.useVendorDocumentType ? file.document_type : file.type;

				return <Stack>
					<Dropdown disabled={uploading || !props.allowSelectDocumentType} selectedKey={selectedKey} options={types} onChange={(evt, opt) => {
						const _files = files;
						if (props.useVendorDocumentType) {
							_files[idx].document_type = (opt?.key || "") as string;
						} else {
							_files[idx].type = (opt?.key || "") as string;
						}

						setFiles([..._files]);
					}} />
				</Stack>;
			}
		},
		{
			key: 'visibility',
			name: 'Visibility',
			minWidth: 80,
			maxWidth: 80,
			onRender: (file: IFileDetailsProps, idx: number = 0) => {
				return <Stack>
					<Dropdown disabled={uploading} selectedKey={file.visibility} options={DocumentVisibilities} onChange={(evt, opt) => {
						const _files = files;
						_files[idx].visibility = (opt?.key || "") as DocumentvisiblityType;

						setFiles([..._files]);
					}} />
				</Stack>
			}
		},
		{
			key: 'expiry',
			name: 'Expiry',
			minWidth: 120,
			maxWidth: 120,
			onRender: (file: IFileDetailsProps, idx: number = 0) => {
				return <Stack>
					<DatePicker disabled={uploading} value={file.expiredDate ? moment(file.expiredDate).toDate() : undefined} formatDate={GeneralService.formatDate} onSelectDate={(date) => {
						const _files = files;
						_files[idx].expiredDate = date ? moment(date).toISOString() : undefined;

						setFiles([..._files]);
					}} />
				</Stack>
			}
		},
		{
			key: 'size',
			name: 'Size',
			minWidth: 80,
			maxWidth: 80,
			onRender: (file: IFileDetailsProps) => {
				return <Stack styles={{ root: { paddingTop: 8, paddingBottom: 8 } }}>
					<Text size={'small'}>{GeneralService.getFriendlyFileSizeText(file.data.size)}</Text>
				</Stack>
			}
		},
		{
			key: 'action',
			name: '',
			minWidth: 69,
			maxWidth: 69,
			onRender: (file: IFileDetailsProps) => {
				return <Stack horizontal tokens={{ childrenGap: 5 }}>
					<TooltipHost title={'Preview'} content={"Preview"}>
						<IconButton iconProps={{ iconName: "View" }} />
					</TooltipHost>
					<TooltipHost title={'Delete'} content={"Delete"}>
						<IconButton iconProps={{ iconName: "Delete" }} onClick={() => _onDeleteFile(file.data)} disabled={uploading} />
					</TooltipHost>
				</Stack>
			}
		}
	];

	/*const renderFileCard = (file: IFileDetailsProps, index: number) => {
		const isExist = (props.existings as any || []).find((ex: any) => {
			if (ex.basename) {
				if (ex.basename.toLowerCase() === file.data.name.toLowerCase()) {
					return true
				}
				return false
			}
			if (ex.name) {
				if (ex.name.toLowerCase() === file.data.name.toLowerCase()) {
					return true
				}
				return false
			}
			return false
		});
		const type = GeneralService.getFileType(file.data.name);

		return <Stack className={styles.item} horizontal tokens={{ childrenGap: 10 }}>
			<Stack.Item className={`${styles.icon} ${styles['bg' + type]}`} align="stretch">
				<Stack styles={{ root: { width: '100%', height: '100%' } }} verticalAlign='center' horizontalAlign='center'>
					{type === 'word' ? <Icon className={styles.image} iconName='WordDocument' /> : null}
					{type === 'excel' ? <Icon className={styles.image} iconName='ExcelDocument' /> : null}
					{type === 'powerpoint' ? <Icon className={styles.image} iconName='PowerPointDocument' /> : null}
					{type === 'pdf' ? <Icon className={styles.image} iconName='PDF' /> : null}
					{type === 'file' ? <FontAwesomeIcon className={styles.image} icon={faFile} /> : null}
					{type === 'image' ? <img className={styles.imagePreview} src={URL.createObjectURL(file.data)} alt={file.data.name} /> : null}
				</Stack>
			</Stack.Item>
			<Stack grow={1} className={styles.label} tokens={{ childrenGap: 10 }}>
				<Stack tokens={{ childrenGap: 5 }}>
					{isExist ? <Stack className={styles.exist} horizontal tokens={{ childrenGap: 10 }}>
						<FontAwesomeIcon icon={faWarning} style={{ marginTop: 1 }} />
						<Text size={'small'}>File with same name already exist. Continue to overwrite existing.</Text>
					</Stack> : null}
					<Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign='space-between'>
						<Label>{file.data.name}</Label>
						<Text size={'small'} style={{ marginTop: 4 }}>{GeneralService.getFriendlyFileSizeText(file.data.size)}</Text>
					</Stack>
				</Stack>
				<Stack className='divider'></Stack>
				<Stack tokens={{ childrenGap: 3 }}>
					{!props.useVendorDocumentType && props.allowSelectDocumentType ? <Stack verticalAlign={'center'} horizontal tokens={{ childrenGap: 5 }}>
						<Text size={'small'}>Type</Text>
						<Text size={'small'}>:</Text>
						<Label size={'xsmall'}>{(props.documentTypes || DocumentTypes).find(d => d.key === file.type)?.text}</Label>
						<ActionButton
							styles={{ root: { height: 'unset !important', minHeight: 'unset !important' } }}
							menuProps={{
								items: (props.documentTypes || DocumentTypes).map(d => ({
									...d, onClick: () => {
										const fls = [...files]
										fls[index].type = d.key;
										setFiles(fls);
									}
								})),
							}}
							iconProps={{ styles: { root: { fontSize: 12 } } }} />
					</Stack> : null}
					{props.useVendorDocumentType && props.allowSelectDocumentType ? <Stack verticalAlign={'center'} horizontal tokens={{ childrenGap: 5 }}>
						<Text size={'small'}>Document Type</Text>
						<Text size={'small'}>:</Text>
						<Label size={'xsmall'}>{VendorDocumentTypes.find(d => d.key === (file as any).document_type)?.text}</Label>
						<ActionButton
							styles={{ root: { height: 'unset !important', minHeight: 'unset !important' } }}
							menuProps={{
								items: VendorDocumentTypes.map(d => ({
									...d, onClick: () => {
										const fls: any = [...files]
										fls[index].document_type = d.key
										setFiles(fls)
									}
								})),
							}}
							iconProps={{ styles: { root: { fontSize: 12 } } }} />
					</Stack> : null}
					<Stack verticalAlign={'center'} horizontal tokens={{ childrenGap: 5 }}>
						<Text size={'small'}>Visibility</Text>
						<Text size={'small'}>:</Text>
						<Label size={'xsmall'}>{DocumentVisibilities.find(d => d.key === file.visibility)?.text}</Label>
						<ActionButton
							styles={{ root: { height: 'unset !important', minHeight: 'unset !important' } }}
							menuProps={{
								items: DocumentVisibilities.map(d => ({
									...d, onClick: () => {
										const fls = [...files]
										fls[index].visibility = d.key as DocumentvisiblityType
										setFiles(fls)
									}
								})),
							}}
							iconProps={{ styles: { root: { fontSize: 12 } } }} />
					</Stack>

				</Stack>
			</Stack>
			<Stack verticalAlign='baseline' className={styles.actions}>
				<ActionButton iconProps={{ iconName: "Delete" }} onClick={() => _onDeleteFile(file.data)} disabled={uploading} />
			</Stack>
		</Stack>
	}*/

	const _onDeleteFile = (file: File) => {
		if (window.confirm(`Are you sure to delete file "${file.name}"?`)) {
			const _files = files.filter((f) => f.data.name !== file.name);
			setFiles(_files);
		}
	}

	const getAllowedFormats = () => {
		let allowedFormats: string[] = [];
		(props.allowedTypes || []).forEach((type) => {
			const formats = FileFormats[type];
			allowedFormats = [...allowedFormats, ...formats];
		});

		return allowedFormats.join(",");
	}

	const _onFilesChanged = (evt: any) => {
		const defaultDocumentType = (props.documentTypes || []).find((dt) => dt.isDefault)?.key || 'document';

		const _files: IFileDetailsProps[] = [];
		for (var ctr = 0; ctr < (evt?.target.files || []).length || 0; ctr++) {
			if (evt && evt.target.files && evt.target.files[ctr]) {
				_files.push({ data: evt.target.files[ctr], visibility: 'private', type: defaultDocumentType });
			}
		}

		if (props.multiple) {
			setFiles([...files, ..._files]);
		} else {
			setFiles([_files[0]]);
		}
	}

	const _onFilesDropped = (evt: any) => {
		const defaultDocumentType = (props.documentTypes || []).find((dt) => dt.isDefault)?.key || 'document';

		evt.preventDefault();

		if (evt.dataTransfer.items) {
			const _files: IFileDetailsProps[] = [];

			[...evt.dataTransfer.items].forEach((item, i) => {
				if (item.kind === "file") {
					const _file = item.getAsFile();
					_files.push({ data: _file, visibility: 'private', type: defaultDocumentType });
				}
			});


			if (props.multiple) {
				setFiles([...files, ..._files]);
			} else {
				setFiles([_files[0]]);
			}
		}
	}

	const _onUpload = async () => {
		try {
			setUploading(true);
			await props.onUpload(files);
			setUploading(false);
		} catch (e) {
			setUploading(false);
		}
	}

	return <Panel headerText={props.title || "Upload File(s)"}
		isOpen={true}
		type={PanelType.custom}
		customWidth={'920px'}
		onDismiss={() => props.onCancel()}
		isFooterAtBottom={true}
		onRenderFooterContent={() => {
			return <Stack horizontal tokens={{ childrenGap: 10 }}>
				{!uploading ? <>
					<PrimaryButton text={'Upload'} onClick={_onUpload} />
					<DefaultButton text={'Cancel'} onClick={() => props.onCancel()} />
				</> : null}
				{uploading ? <LoadingComponent label='Uploading ...' labelPosition='right' spinnerPosition='baseline' /> : null}
			</Stack>;
		}}>
		<Stack className={styles.container} tokens={{ childrenGap: 20 }}>
			<Dropzone multiple={props.multiple} disabled={uploading}>
				{({ getRootProps, getInputProps }) => (
					<Stack tokens={{ childrenGap: 20 }} {...getRootProps({
						value: files,
						onChange: _onFilesChanged,
						onDrop: _onFilesDropped,
						className: `${styles.upload} dropzone`,
						for: 'upload_files'
					})}>
						<input {...getInputProps({ id: 'upload_files' })} />
						<FontAwesomeIcon icon={faUpload} fontSize={25} />
						<Stack tokens={{ childrenGap: 0 }} horizontalAlign='center'>
							<Label>Drop your files here</Label>
							<Text size={'small'}>or <Link>click here</Link> to browse from your computer</Text>
						</Stack>
					</Stack>
				)}
			</Dropzone>
			{files.length > 0 ? <>
				<Text className={'color-red'} style={{fontStyle: 'italic'}}>Nb: files/documents with "Public" visibility means everyone (including customers) can read this documents. If files/documents are meant for internal only, please set visibility to "Private"</Text>
				<DetailsList items={files} columns={columns} selectionMode={SelectionMode.none} />
			</> : null}
		</Stack>
	</Panel>;
};

export default UploadFilesPanel;
